/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Header search-suggestions keyboard navigation.
 *
 * The combobox input owns the highlight state and drives the popover
 * via `aria-activedescendant` — DOM focus stays on the input so the
 * existing `onBlur={() => setShowSuggestions(false)}` keeps working.
 * Suggestion rows publish their stable id to the context so mouse
 * hover and keyboard arrows share one highlight source of truth.
 */
import { createContext, useContext } from 'react';

export interface KeyboardNavContextValue {
    /** DOM id of the currently highlighted row, or undefined when no row is active. */
    activeId: string | undefined;
    /** Mouse hover sync — promotes the row at `id` to be the active descendant. */
    setHighlightedById: (id: string) => void;
}

// Outside the SearchBar provider the highlight callback is a no-op —
// the explicit assignment keeps `@typescript-eslint/no-empty-function`
// happy without forcing every consumer to defend against `undefined`.
const noop = (): void => undefined;

const DEFAULT_VALUE: KeyboardNavContextValue = {
    activeId: undefined,
    setHighlightedById: noop,
};

export const SuggestionsKeyboardContext = createContext<KeyboardNavContextValue>(DEFAULT_VALUE);

export function useSuggestionsKeyboardNav(): KeyboardNavContextValue {
    return useContext(SuggestionsKeyboardContext);
}
